// src/pages/Catalog/ServiceMainCategoriesPage.jsx
//
// Service Main Categories page
// ============================
//
// PURPOSE
// -------
// This page manages the top-level service catalog categories used by employees
// inside hb_visits.
//
// Examples:
// - Hairdressing
// - Cosmetology
// - Spa & Relax
// - Health
//
// UX goals
// --------
// 1) Keep the page readable and explicit.
// 2) Use one stable table area with internal scroll.
// 3) Keep editor actions on the same page (no separate sub-route needed).
// 4) Avoid unnecessary refetch patterns or over-abstraction.
// 5) Use the service layer for API calls and keep UI messages local.
//
// Architectural note
// ------------------
// This page is part of the operational Catalog area, not an admin-only master-data screen.
// That means it should feel practical and accessible to normal authenticated staff.
//

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ServiceMainCategoriesPage.css";

import {
  fetchServiceMainCategories,
  createServiceMainCategory,
  updateServiceMainCategory,
  toggleServiceMainCategoryActive,
  deleteServiceMainCategory,
} from "../../services/serviceMainCategoriesService";

/* -------------------------------------------------------------------------- */
/* Local helpers                                                              */
/* -------------------------------------------------------------------------- */

function asBool(v) {
  return v === true || String(v).trim().toLowerCase() === "true" || String(v).trim() === "1";
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function slugify(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 150);
}

function initialForm() {
  return {
    name_internal: "",
    name_public: "",
    slug: "",
    description_short: "",
    description_long: "",
    sort_order: 0,
    is_active: true,
    publish_to_website: true,
  };
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function ServiceMainCategoriesPage() {
  const { t } = useTranslation("catalog");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("CREATE"); // CREATE | EDIT
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm());

  const activeCount = useMemo(() => rows.filter((r) => asBool(r.is_active)).length, [rows]);

  async function load() {
    setLoading(true);
    setError("");

    try {
      const data = await fetchServiceMainCategories();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || t("serviceMainCategories.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openCreate() {
    setEditorMode("CREATE");
    setEditingId(null);
    setForm(initialForm());
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  function openEdit(row) {
    setEditorMode("EDIT");
    setEditingId(Number(row.id));
    setForm({
      name_internal: row.name_internal || "",
      name_public: row.name_public || "",
      slug: row.slug || "",
      description_short: row.description_short || "",
      description_long: row.description_long || "",
      sort_order: Number(row.sort_order || 0),
      is_active: asBool(row.is_active),
      publish_to_website: asBool(row.publish_to_website),
    });
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingId(null);
    setForm(initialForm());
  }

  function handleNamePublicBlurAutoSlug() {
    // Small UX helper:
    // if slug is still empty, derive it from public name once the user leaves the field.
    setForm((prev) => {
      if ((prev.slug || "").trim() !== "") return prev;
      return { ...prev, slug: slugify(prev.name_public) };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...form,
        sort_order: toNumberOrZero(form.sort_order),
      };

      if (editorMode === "CREATE") {
        await createServiceMainCategory(payload);
        setSuccess(t("serviceMainCategories.messages.created"));
      } else {
        await updateServiceMainCategory(editingId, payload);
        setSuccess(t("serviceMainCategories.messages.updated"));
      }

      closeEditor();
      await load();
    } catch (e2) {
      setError(e2?.message || t("serviceMainCategories.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(row) {
    setError("");
    setSuccess("");

    try {
      await toggleServiceMainCategoryActive(row.id, !asBool(row.is_active));
      setSuccess(
        t("serviceMainCategories.messages.statusChanged", {
          status: asBool(row.is_active)
            ? t("serviceMainCategories.status.deactivated")
            : t("serviceMainCategories.status.activated"),
        })
      );
      await load();
    } catch (e) {
      setError(e?.message || t("serviceMainCategories.messages.statusChangeFailed"));
    }
  }

  /**
   * Delete handler.
   *
   * IMPORTANT
   * ---------
   * Delete button is always visible on the page.
   * Backend decides whether physical delete is allowed.
   *
   * If backend blocks the delete, we surface the returned business message
   * directly to the user.
   */
  async function handleDelete(row) {
    const confirmed = window.confirm(
      t("serviceMainCategories.confirmDelete", { name: row.name_public })
    );

    if (!confirmed) return;

    setError("");
    setSuccess("");

    try {
      await deleteServiceMainCategory(row.id);

      if (editorOpen && Number(editingId) === Number(row.id)) {
        closeEditor();
      }

      setSuccess(t("serviceMainCategories.messages.deleted"));
      await load();
    } catch (e) {
      setError(e?.message || t("serviceMainCategories.messages.deleteFailed"));
    }
  }

  return (
    <div className="smc-page">
      <div className="smc-page__inner">
        <div className="smc-page__header">
          <div>
            <h1>{t("serviceMainCategories.title")}</h1>
            <p>
              {t("serviceMainCategories.subtitle")}
            </p>
          </div>

          <div className="smc-page__headerActions">
            <button type="button" className="smc-btn smc-btn--primary" onClick={openCreate}>
              {t("serviceMainCategories.actions.add")}
            </button>
          </div>
        </div>

        <div className="smc-page__card">
          <div className="smc-page__meta">
            <span>{t("serviceMainCategories.meta.total", { count: rows.length })}</span>
            <span>{t("serviceMainCategories.meta.active", { count: activeCount })}</span>
          </div>

          {error ? <div className="smc-alert smc-alert--error">{error}</div> : null}
          {success ? <div className="smc-alert smc-alert--success">{success}</div> : null}

          {editorOpen && (
            <form className="smc-editor" onSubmit={handleSubmit}>
              <div className="smc-editor__grid">
                <div className="smc-field">
                  <label>{t("serviceMainCategories.fields.internalName")}</label>
                  <input
                    value={form.name_internal}
                    onChange={(e) => setForm((prev) => ({ ...prev, name_internal: e.target.value }))}
                    required
                  />
                </div>

                <div className="smc-field">
                  <label>{t("serviceMainCategories.fields.publicName")}</label>
                  <input
                    value={form.name_public}
                    onChange={(e) => setForm((prev) => ({ ...prev, name_public: e.target.value }))}
                    onBlur={handleNamePublicBlurAutoSlug}
                    required
                  />
                </div>

                <div className="smc-field">
                  <label>{t("serviceMainCategories.fields.slug")}</label>
                  <input
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                    placeholder={t("serviceMainCategories.placeholders.autoSlug")}
                  />
                </div>

                <div className="smc-field">
                  <label>{t("serviceMainCategories.fields.sortOrder")}</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
                  />
                </div>

                <div className="smc-field smc-field--full">
                  <label>{t("serviceMainCategories.fields.shortDescription")}</label>
                  <input
                    value={form.description_short}
                    onChange={(e) => setForm((prev) => ({ ...prev, description_short: e.target.value }))}
                  />
                </div>

                <div className="smc-field smc-field--full">
                  <label>{t("serviceMainCategories.fields.longDescription")}</label>
                  <textarea
                    rows="4"
                    value={form.description_long}
                    onChange={(e) => setForm((prev) => ({ ...prev, description_long: e.target.value }))}
                  />
                </div>
              </div>

              <div className="smc-editor__checks">
                <label>
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>{t("serviceMainCategories.fields.active")}</span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={!!form.publish_to_website}
                    onChange={(e) => setForm((prev) => ({ ...prev, publish_to_website: e.target.checked }))}
                  />
                  <span>{t("serviceMainCategories.fields.publishToWebsite")}</span>
                </label>
              </div>

              <div className="smc-editor__actions">
                <button type="submit" className="smc-btn smc-btn--primary" disabled={saving}>
                  {saving
                    ? t("serviceMainCategories.actions.saving")
                    : editorMode === "CREATE"
                      ? t("serviceMainCategories.actions.create")
                      : t("serviceMainCategories.actions.saveChanges")}
                </button>
                <button type="button" className="smc-btn" onClick={closeEditor} disabled={saving}>
                  {t("serviceMainCategories.actions.cancel")}
                </button>
              </div>
            </form>
          )}

          <div className="smc-tableArea">
            <div className="smc-tableWrap">
              <table className="smc-table">
                <thead>
                  <tr>
                    <th className="smc-left">{t("serviceMainCategories.columns.publicName")}</th>
                    <th className="smc-left">{t("serviceMainCategories.columns.slug")}</th>
                    <th className="smc-left">{t("serviceMainCategories.columns.sort")}</th>
                    <th className="smc-left">{t("serviceMainCategories.columns.active")}</th>
                    <th className="smc-left">{t("serviceMainCategories.columns.publish")}</th>
                    <th className="smc-right">{t("serviceMainCategories.columns.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="smc-empty">{t("serviceMainCategories.empty")}</td>
                    </tr>
                  ) : null}

                  {rows.map((row) => (
                    <tr key={row.id} className={!asBool(row.is_active) ? "smc-rowMuted" : ""}>
                      <td className="smc-left">{row.name_public}</td>
                      <td className="smc-left"><code>{row.slug}</code></td>
                      <td className="smc-left">{row.sort_order}</td>
                      <td className="smc-left">{asBool(row.is_active) ? t("serviceMainCategories.boolean.yes") : t("serviceMainCategories.boolean.no")}</td>
                      <td className="smc-left">{asBool(row.publish_to_website) ? t("serviceMainCategories.boolean.yes") : t("serviceMainCategories.boolean.no")}</td>
                      <td className="smc-right">
                        <div className="smc-actions">
                          <button type="button" className="smc-btn smc-btn--small" onClick={() => openEdit(row)}>
                            {t("serviceMainCategories.actions.edit")}
                          </button>
                          <button
                            type="button"
                            className="smc-btn smc-btn--small"
                            onClick={() => handleToggleActive(row)}
                          >
                            {asBool(row.is_active)
                              ? t("serviceMainCategories.actions.deactivate")
                              : t("serviceMainCategories.actions.activate")}
                          </button>
                          <button
                            type="button"
                            className="smc-btn smc-btn--small smc-btn--danger"
                            onClick={() => handleDelete(row)}
                            title={t("serviceMainCategories.actions.deleteTitle")}
                          >
                            {t("serviceMainCategories.actions.delete")}
                          </button>                         
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


